import React, { useMemo, useState } from 'react';
import { Typography, Card, Space, Button, Table, Tabs, InputNumber, Form, TimePicker, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useBiathlonEvent } from '../contexts/BiathlonEventContext';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;

interface ClubSummary {
  name: string;
  count: number;
}

interface TeamSummary {
  name: string;
  count: number;
}

const FordeleSkiver: React.FC = () => {
  const { selectedEvent, athletes, loading } = useBiathlonEvent();
  const navigate = useNavigate();
  const [antallSkiver, setAntallSkiver] = useState<number>(30);
  const [startTid, setStartTid] = useState<Dayjs | null>(null);
  const [sluttTid, setSluttTid] = useState<Dayjs | null>(null);
  const [skiveAllocation, setSkiveAllocation] = useState<{[skiveNumber: number]: {clubs: string[], athletes: number}}>({});

  const laneCalculation = useMemo(() => {
    if (athletes.length === 0 || antallSkiver === 0) {
      return { athletesPerLane: 0, remainder: 0, lanesWithExtra: 0, minutterPerUtover: null, totalMinutter: null, maxAthletesPerLane: 0 };
    }

    const athletesPerLane = Math.floor(athletes.length / antallSkiver);
    const remainder = athletes.length % antallSkiver;
    const lanesWithExtra = remainder;
    const maxAthletesPerLane = remainder > 0 ? athletesPerLane + 1 : athletesPerLane;

    let minutterPerUtover = null;
    let totalMinutter = null;

    if (startTid && sluttTid) {
      // Calculate total minutes between start and end time
      totalMinutter = sluttTid.diff(startTid, 'minute');
      if (totalMinutter > 0 && maxAthletesPerLane > 0) {
        // Time per athlete is total time divided by max athletes per lane
        // (since all lanes run in parallel)
        minutterPerUtover = totalMinutter / maxAthletesPerLane;
      }
    }

    return { athletesPerLane, remainder, lanesWithExtra, minutterPerUtover, totalMinutter, maxAthletesPerLane };
  }, [athletes.length, antallSkiver, startTid, sluttTid]);

  const handleFordelSkiver = () => {
    const clubCounts: { [key: string]: number } = {};
    
    // Count athletes per club
    athletes.forEach(athlete => {
      const clubName = athlete.Utover.Klubb.Navn;
      clubCounts[clubName] = (clubCounts[clubName] || 0) + 1;
    });

    // Sort clubs by athlete count (descending)
    const sortedClubs = Object.entries(clubCounts)
      .sort((a, b) => b[1] - a[1]);

    const allocation: {[skiveNumber: number]: {clubs: string[], athletes: number}} = {};
    let currentSkive = 1;
    const usedClubs = new Set<string>();

    // First pass: Assign large clubs that need multiple lanes
    for (const [clubName, count] of sortedClubs) {
      if (usedClubs.has(clubName)) continue;
      
      if (count > laneCalculation.maxAthletesPerLane) {
        // Club needs multiple lanes
        const lanesNeeded = Math.ceil(count / laneCalculation.maxAthletesPerLane);
        for (let i = 0; i < lanesNeeded; i++) {
          if (currentSkive > antallSkiver) {
            message.warning('Ikke nok skiver tilgjengelig for alle klubber. Øk antall skiver.');
            return;
          }
          allocation[currentSkive] = {
            clubs: [clubName],
            athletes: Math.min(laneCalculation.maxAthletesPerLane, count - i * laneCalculation.maxAthletesPerLane)
          };
          currentSkive++;
        }
        usedClubs.add(clubName);
      }
    }

    // Second pass: Pack smaller clubs together, filling lanes as much as possible
    const remainingClubs = sortedClubs.filter(([club]) => !usedClubs.has(club));
    
    let currentLaneClubs: string[] = [];
    let currentLaneAthletes = 0;
    
    for (const [clubName, count] of remainingClubs) {
      if (usedClubs.has(clubName)) continue;
      
      // Check if this club fits in the current lane
      if (currentLaneAthletes + count <= laneCalculation.maxAthletesPerLane) {
        currentLaneClubs.push(clubName);
        currentLaneAthletes += count;
        usedClubs.add(clubName);
      } else {
        // Current lane is full, save it and start a new one
        if (currentLaneClubs.length > 0) {
          if (currentSkive > antallSkiver) {
            message.warning('Ikke nok skiver tilgjengelig for alle klubber. Øk antall skiver.');
            return;
          }
          allocation[currentSkive] = {
            clubs: [...currentLaneClubs],
            athletes: currentLaneAthletes
          };
          currentSkive++;
        }
        
        // Start new lane with current club
        currentLaneClubs = [clubName];
        currentLaneAthletes = count;
        usedClubs.add(clubName);
      }
    }
    
    // Don't forget the last lane if it has clubs
    if (currentLaneClubs.length > 0) {
      if (currentSkive > antallSkiver) {
        message.warning('Ikke nok skiver tilgjengelig for alle klubber. Øk antall skiver.');
        return;
      }
      allocation[currentSkive] = {
        clubs: [...currentLaneClubs],
        athletes: currentLaneAthletes
      };
    }

    setSkiveAllocation(allocation);
    message.success(`Skiver fordelt! ${Object.keys(allocation).length} av ${antallSkiver} skiver i bruk.`);
  };

  const clubSummary = useMemo(() => {
    const clubCounts: { [key: string]: number } = {};
    
    athletes.forEach(athlete => {
      const clubName = athlete.Utover.Klubb.Navn;
      clubCounts[clubName] = (clubCounts[clubName] || 0) + 1;
    });

    return Object.entries(clubCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [athletes]);

  const teamSummary = useMemo(() => {
    const teamCounts: { [key: string]: number } = {};
    
    athletes.forEach(athlete => {
      const teamName = athlete.Utover.Team?.Navn;
      if (teamName) {
        teamCounts[teamName] = (teamCounts[teamName] || 0) + 1;
      }
    });

    return Object.entries(teamCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [athletes]);

  const clubColumns: ColumnsType<ClubSummary> = [
    {
      title: 'Klubb',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Antall løpere',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: 'descend',
    },
  ];

  const teamColumns: ColumnsType<TeamSummary> = [
    {
      title: 'Team',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Antall løpere',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: 'descend',
    },
  ];

  if (!selectedEvent) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <Title level={3}>No Event Selected</Title>
            <Text>Please select an event from the home page first.</Text>
            <Button type="primary" onClick={() => navigate('/')}>
              Go to Home
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <header style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Title level={2} style={{ margin: 0 }}>Fordele Skiver</Title>
          <div style={{ textAlign: 'right' }}>
            <Text strong>Event: {selectedEvent?.Name || 'No event selected'}</Text>
            <br />
            <Text type="secondary">
              Totalt antall løpere: {athletes.length}
            </Text>
          </div>
        </div>
      </header>

      <Card>
        <Tabs 
          defaultActiveKey="clubs"
          items={[
            {
              key: 'clubs',
              label: `Klubber (${clubSummary.length})`,
              children: (
                <Table
                  columns={clubColumns}
                  dataSource={clubSummary}
                  rowKey="name"
                  loading={loading}
                  size="small"
                  pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                  summary={(data) => (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <strong>Total</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <strong>{data.reduce((sum, item) => sum + item.count, 0)}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              )
            },
            {
              key: 'teams',
              label: `Team (${teamSummary.length})`,
              children: (
                <Table
                  columns={teamColumns}
                  dataSource={teamSummary}
                  rowKey="name"
                  loading={loading}
                  size="small"
                  pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                  summary={(data) => (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <strong>Total</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <strong>{data.reduce((sum, item) => sum + item.count, 0)}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              )
            },
            {
              key: 'oppsett',
              label: 'Oppsett',
              children: (
                <div style={{ padding: '24px' }}>
                  <Form layout="vertical">
                    <Form.Item label="Antall skiver">
                      <InputNumber
                        min={1}
                        max={40}
                        value={antallSkiver}
                        onChange={(value) => setAntallSkiver(value || 30)}
                        style={{ width: '200px' }}
                      />
                    </Form.Item>

                    <Form.Item label="Innskytingstid">
                      <Space>
                        <TimePicker
                          format="HH:mm"
                          placeholder="Fra"
                          value={startTid}
                          onChange={(time) => setStartTid(time)}
                          style={{ width: '120px' }}
                        />
                        <span>-</span>
                        <TimePicker
                          format="HH:mm"
                          placeholder="Til"
                          value={sluttTid}
                          onChange={(time) => setSluttTid(time)}
                          style={{ width: '120px' }}
                        />
                      </Space>
                    </Form.Item>
                  </Form>

                  <Card style={{ marginTop: '24px', backgroundColor: '#f5f5f5' }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Title level={4}>Fordeling</Title>
                      <div>
                        <Text strong>Totalt antall løpere: </Text>
                        <Text>{athletes.length}</Text>
                      </div>
                      <div>
                        <Text strong>Antall skiver: </Text>
                        <Text>{antallSkiver}</Text>
                      </div>
                      {laneCalculation.totalMinutter !== null && (
                        <div>
                          <Text strong>Total innskytingstid: </Text>
                          <Text>{laneCalculation.totalMinutter} minutter</Text>
                        </div>
                      )}
                      {laneCalculation.minutterPerUtover !== null && (
                        <div>
                          <Text strong>Tid per utøver: </Text>
                          <Text>
                            {Math.floor(laneCalculation.minutterPerUtover)} min {Math.round((laneCalculation.minutterPerUtover % 1) * 60)} sek
                          </Text>
                        </div>
                      )}
                      <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: '12px', marginTop: '12px' }}>
                        <Text strong style={{ fontSize: '16px' }}>Løpere per skive: </Text>
                        <Text style={{ fontSize: '16px' }}>
                          {laneCalculation.athletesPerLane}
                          {laneCalculation.remainder > 0 && (
                            <span>
                              {' '}- {laneCalculation.athletesPerLane + 1}
                            </span>
                          )}
                        </Text>
                      </div>
                      {laneCalculation.remainder > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {antallSkiver - laneCalculation.lanesWithExtra} skiver får {laneCalculation.athletesPerLane} løpere, 
                            {' '}{laneCalculation.lanesWithExtra} skiver får {laneCalculation.athletesPerLane + 1} løpere.
                          </Text>
                        </div>
                      )}
                    </Space>
                  </Card>

                  {Object.keys(skiveAllocation).length === 0 ? (
                    <div style={{ marginTop: '24px', textAlign: 'center' }}>
                      <Button type="primary" size="large" onClick={handleFordelSkiver}>
                        Fordel Skiver
                      </Button>
                    </div>
                  ) : (
                    <div style={{ marginTop: '24px' }}>
                      <Card>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <Title level={4} style={{ margin: 0 }}>Skivefordeling</Title>
                          <Button onClick={handleFordelSkiver}>
                            Fordel på nytt
                          </Button>
                        </div>
                        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                          {Object.entries(skiveAllocation)
                            .sort((a, b) => Number(a[0]) - Number(b[0]))
                            .map(([skiveNum, data]) => (
                              <div 
                                key={skiveNum}
                                style={{ 
                                  padding: '12px', 
                                  border: '1px solid #d9d9d9', 
                                  borderRadius: '4px', 
                                  marginBottom: '8px',
                                  backgroundColor: '#fafafa'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <Text strong>Skive {skiveNum}: </Text>
                                    <Text>{data.clubs.join(' + ')}</Text>
                                  </div>
                                  <Text type="secondary">{data.athletes} løpere</Text>
                                </div>
                              </div>
                            ))}
                        </div>
                      </Card>
                    </div>
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default FordeleSkiver;
